import { Emoji } from '@gitbook/spine-react';
import { getEmojiShortnameForCode } from '@gitbook/util-emoji-rendering';
import type { EmojiDef } from '@gitbook/util-emojis';

import { createPalette } from '../transforms';
import type { PaletteDef } from '../types';

/**
 * List of top emojis to suggest to users.
 */
const EMOJIS_SUGGESTION = ['1f44d', '1f389', '1f604', '1f602', '1f44f', '1f44e'].map((code) => ({
    code,
    shortname: getEmojiShortnameForCode(code) ?? code,
}));

let emojiCatalog: typeof import('@gitbook/util-emojis') | null = null;
let emojiCatalogPromise: Promise<typeof import('@gitbook/util-emojis')> | null = null;

function getEmojiCatalog(): typeof import('@gitbook/util-emojis') {
    if (emojiCatalog) {
        return emojiCatalog;
    }

    emojiCatalogPromise ??= import('@gitbook/util-emojis').then((catalog) => {
        emojiCatalog = catalog;
        return catalog;
    });

    throw emojiCatalogPromise;
}

/**
 * Create a commands palette to pick an emoji.
 */
export function createPickEmojiPalette(
    pick: (code: string) => void,
    options: {
        /**
         * Maximum number of emojis shown as suggestions
         */
        max?: number;

        /**
         * Palette options.
         */
        withSearch?: boolean;
    } = {}
): PaletteDef {
    const { withSearch = true, max = 5 } = options;

    return createPalette({
        search: withSearch ? { placeholder: 'Select an emoji…' } : undefined,
        groups: [
            {
                key: 'emojis',
                commands: (query) => {
                    const emojis: Array<Pick<EmojiDef, 'code' | 'shortname'>> = query
                        ? getEmojiCatalog().searchEmojis(query)
                        : EMOJIS_SUGGESTION;

                    return emojis.slice(0, max).map((emoji) => {
                        return {
                            key: emoji.code,
                            title: emoji.shortname,
                            icon: <Emoji code={emoji.code} />,
                            trigger: () => pick(emoji.code),
                        };
                    });
                },
            },
        ],
    });
}
